import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { AnalysisResultToStateColor } from '../components/enums';
import axiosInstance from './axios';
import { isValidEmail, isValidName, isValidTimeZone } from '../components/validation';
import { TIME_ZONES, GetUserTimeZone } from '../components/time_zones';
import Toast from '../components/toast';
import TempPassDialog from '../components/temp_password';
import NoItemsOverlay from '../components/no_items_overlay';
import SelectAllControl from './select_all_control';
import LocationHierarchyFilterControls from './filters/location_hierarchy_filter_controls';

import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import {
	DataGrid,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	useGridApiRef,
	gridFilteredSortedRowEntriesSelector,
} from '@mui/x-data-grid';

// Helper to generate a class name from a color
function colorToClassName(color) {
	if (!color) return '';
	return 'row-color-' + color.replace('#', '').toLowerCase();
}

// DataGrid getRowClassName implementation
const getRowClassName = (params) => {
	const color = AnalysisResultToStateColor(params.row.system_analysis);
	return color ? colorToClassName(color) : '';
};

function CreateUserLocationAccessToolbar({ onSelectAll, selectAllState, onHierarchyFilterChange }) {
	return (
		<GridToolbarContainer>
			<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
				<Box sx={{ display: 'flex', gap: 1 }}>
					<GridToolbarColumnsButton />
					<GridToolbarFilterButton />
					<SelectAllControl
						onSelectAll={onSelectAll}
						selectAllState={selectAllState}
						columnName='Has Access'
						itemType='locations'
					/>
				</Box>
				<Box sx={{ marginLeft: 'auto' }}>
					<LocationHierarchyFilterControls onFilterChange={onHierarchyFilterChange} />
				</Box>
			</Box>
		</GridToolbarContainer>
	);
}

export default function CreateUserDialog({ creatorUser, dialogOpen, setDialogOpen, cloneUser }) {

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
		clickToClose: true,
		duration: 3000,
	});
	const [tempPassOpen, setTempPassOpen] = useState({ open: false, user: '', password: '' });
	
	const [selectAllState, setSelectAllState] = useState(true);

	// Form Fields
	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [jobTitle, setJobTitle] = useState('');
	const [company, setCompany] = useState('');
	const [timeZone, setTimeZone] = useState(GetUserTimeZone());
	const [metric, setMetric] = useState(false);
	const [isActive, setIsActive] = useState(true);
	const [isAdmin, setIsAdmin] = useState(false);
	const [isSuperuser, setIsSuperuser] = useState(false);

	// Form Validity
	const [emailValid, setEmailValid] = useState(false);
	const [firstNameValid, setFirstNameValid] = useState(false);
	const [lastNameValid, setLastNameValid] = useState(false);
	const [timeZoneValid, setTimeZoneValid] = useState(false);

	// Location access
	const [accessStep, setAccessStep] = useState(false);
	const [locationInfo, setLocationInfo] = useState([]);
	const [locationAccess, setLocationAccess] = useState([]);

	// DataGrid pagination state
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [hierarchyFilters, setHierarchyFilters] = useState({
		org: '',
		reg: '',
		fac: '',
		bldg: '',
	});

	const apiRef = useGridApiRef();

	// Verify all form requirements are met
	const infoRequirementsMet = emailValid && firstNameValid && lastNameValid && timeZoneValid;
	
	// Form Validity checks
	useEffect(() => { setEmailValid(isValidEmail(email)); }, [email]);
	
	useEffect(() => { setFirstNameValid(isValidName(firstName)); }, [firstName]);
	
	useEffect(() => { setLastNameValid(isValidName(lastName)); }, [lastName]);
	
	useEffect(() => { setTimeZoneValid(isValidTimeZone(timeZone)); }, [timeZone]);

	// Clear the contents of the dialog
	const clearDialog = () => {
		setEmail('');
		setFirstName('');
		setLastName('');
		setJobTitle('');
		setCompany('');
		setTimeZone(GetUserTimeZone());
		setMetric(false);
		setIsActive(true);
		setIsAdmin(false);
		setIsSuperuser(false);
		setLocationAccess(locationAccess.map(access => ({ ...access, has_access: false })));
	};

	// Clone User functionality
	useEffect(() => {
		if (cloneUser) {
			const isSuperuser = !!cloneUser.is_superuser;

			setEmail('');
			setFirstName('');
			setLastName('');
			setJobTitle('');
			setCompany(cloneUser.company || '');
			setTimeZone(cloneUser.time_zone || '');
			setMetric(!!cloneUser.metric);
			setIsActive(!!cloneUser.is_active);
			setIsAdmin(!!cloneUser.is_staff);
			setIsSuperuser(isSuperuser);

			// no need to check for permissions if the user is a superuser
			if (!isSuperuser) {
				getCloneUserPermissions();
			}
		}
	}, [cloneUser]);

	// Get the location information and access permissions
	useEffect(() => {
		// Get the location information
		axiosInstance.get('/map')
			.then(response => {
				setLocationInfo(response.data.locations);
			})
			.catch(error => {
				setToast({
					open: true,
					severity: 'error',
					message: 'Error loading location information. ' + error,
					clickToClose: true,
					duration: 3000,
				});
				console.error(error);
			});

		// Get the location access permissions
		axiosInstance.get('/users/locations/')
			.then(response => {
				const accessData = response.data.map(location => ({
					...location,
					has_access: false,
					change_system_settings: false,
					change_limits: false,
					change_email_settings: false,
					change_observer_settings: false,
					view_audit_log: false,
					view_notes: false,
				}));
				setLocationAccess(accessData);
			})
			.catch(error => {
				setToast({
					open: true,
					severity: 'error',
					message: 'Error loading location access information. ' + error,
					clickToClose: true,
					duration: 3000,
				});
				console.error(error);
			});
	}, []);

	// Filter and combine location data based on hierarchy filters
	const combinedData = useMemo(() => {
		const combined = locationInfo.map(location => {
			const access = locationAccess.find(access => access.location_id === location.location_id);
			return { ...location, ...access };
		});

		// Apply hierarchy filters
		return combined.filter(location => {
			if (hierarchyFilters.org && location.organization !== hierarchyFilters.org) {
				return false;
			}
			if (hierarchyFilters.reg && location.region !== hierarchyFilters.reg) {
				return false;
			}
			if (hierarchyFilters.fac && location.facility !== hierarchyFilters.fac) {
				return false;
			}
			if (hierarchyFilters.bldg && location.building !== hierarchyFilters.bldg) {
				return false;
			}
			return true;
		});
	}, [locationInfo, locationAccess, hierarchyFilters]);

	// Handle hierarchy filter changes
	const handleHierarchyFilterChange = useCallback((nextFilters) => {
		setHierarchyFilters(nextFilters);
		setPaginationModel((prevPagination) => (
			prevPagination.page === 0 ? prevPagination : { ...prevPagination, page: 0 }
		));
	}, []);

	// Get locations permissions for the user to be cloned and set those for the new user
	function getCloneUserPermissions() {
		if (!cloneUser) return;

		axiosInstance.get(`/users/${cloneUser.id}/locations/`)
			.then(response => {
				const clonePermissions = response.data;

				// Create a lookup for quick access
				const permLookup = {};
				clonePermissions.forEach(perm => {
					permLookup[perm.location_id] = perm;
				});

				setLocationAccess(prevAccess =>
					prevAccess.map(access => {
						const match = permLookup[access.location_id];
						if (match) {
							return {
								...access,
								has_access: true,
								change_system_settings: match.change_system_settings,
								change_limits: match.change_limits,
								change_email_settings: match.change_email_settings,
								change_observer_settings: match.change_observer_settings,
								view_audit_log: match.view_audit_log,
								view_notes: match.view_notes,
							};
						}
						return access;
					})
				);
			})
			.catch(error => {
				setToast({
					open: true,
					severity: 'error',
					message: 'Error loading location access information. ' + error,
					clickToClose: true,
					duration: 3000,
				});
				console.error(error);
			});
	}

	// Render a switch for a given locationID and switchName
	const renderSwitch = (locationId, switchName, checked) => {
		return (
			<div style={{ width: '100%', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Switch
					checked={checked}
					onChange={(event) => handlePermissionToggle(locationId, switchName)}
					color='primary'
					sx={{ backgroundColor: '#ffffff77', borderRadius: '20px' }}
				/>
			</div>
		);
	}

	// User List table columns
	// TODO: MODIFY THE FUTURE PERMISSIONS TO DISABLE IF THE USER DOES NOT HAVE ACCESS TO THE LOCATION
	const columns = [
		{
			field: 'has_access',
			headerName: 'Access',
			headerAlign: "center",
			description: 'Can the User view this location?',
			minWidth: 100,
			flex: 1,
			filterable: false,
			hideable: false,
			renderCell: (params) => renderSwitch(params.row.location_id, 'has_access', params.value),
		},
		// {
		// 	field: 'change_system_settings',
		// 	headerName: 'System Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s system settings (not including limits)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_system_settings', params.value),
		// },
		// {
		// 	field: 'change_limits',
		// 	headerName: 'Change Limits',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s limit settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_limits', params.value),
		// },
		// {
		// 	field: 'change_email_settings',
		// 	headerName: 'Email Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s email settings (for all users)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_email_settings', params.value),
		// },
		// {
		// 	field: 'change_observer_settings',
		// 	headerName: 'Observer Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s observer settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_observer_settings', params.value),
		// },
		// {
		// 	field: 'view_audit_log',
		// 	headerName: 'View Audit Log',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Audit Log?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'view_audit_log', params.value),
		// },
		// {
		// 	field: 'view_notes',
		// 	headerName: 'View Notes',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Notes column across the site?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'view_notes', params.value),
		// },
		{
			field: 'location_name',
			headerName: 'Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Location Name',
			minWidth: 250,
			flex: 1,
		},
		{
			field: 'serial_number',
			headerName: 'Serial #',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Serial Number',
			minWidth: 75,
			flex: 1,
		},
		{
			field: 'bvs_model',
			headerName: 'Model',
			headerAlign: "center",
			headerClassName: 'wrap-header-text' ,
			description: 'BVS Controller Model',
			minWidth: 100,
			flex: 1,
		},
		{
			field: 'organization',
			headerName: 'Organization',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Organization Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'region',
			headerName: 'Region',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Region Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'facility',
			headerName: 'Facility',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Facility Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'building',
			headerName: 'Building',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Building Hierarchy',
			minWidth: 150,
			flex: 1,
		},
	];

	// Get the locations chosen for the new user to have access to and which permissions
	function getSelectedLocations() {
		return locationAccess
			.filter(access => access.has_access)
			.map(location => ({
				location_id: location.location_id,
				change_system_settings: location.change_system_settings,
				change_limits: location.change_limits,
				change_email_settings: location.change_email_settings,
				change_observer_settings: location.change_observer_settings,
				view_audit_log: location.view_audit_log,
				view_notes: location.view_notes,
			}));
	}

	// Call self password reset API endpoint
	function createUser() {
		// Get the new user's location permissions
		let locPermissions = [];
		if (!isSuperuser || !creatorUser.is_superuser) {
			locPermissions = getSelectedLocations();
		}

		const newUser = {
			email: email,
			password_reset: true,
			first_name: firstName,
			last_name: lastName,
			job_title: jobTitle,
			company: company,
			time_zone: timeZone,
			metric: metric,
			is_active: isActive,
			is_staff: isAdmin,
			is_superuser: creatorUser.is_superuser ? isSuperuser : false,
		};

		const requestData = {
			new_user: newUser,
			location_permissions: locPermissions,
		};

		axiosInstance.post('users/create/', requestData)
			.then(response => {
				// Call the password reset API endpoint
				axiosInstance.post(`password_reset/`, {
					email: email,
				})
				.then((res) => {
					if (res.status === 200) {
						setToast({ open: true, severity: 'success', message: 'An email has been sent to the new user to finish setting up their account.', duration: 5000, clickToClose: true });
					} else {
						emailSendFailure(`${firstName} ${lastName}`, response.data.new_password);
					}
				})
				.catch((error) => {
					console.log('Error trying to reset the new user\'s password:', error);
					emailSendFailure(`${firstName} ${lastName}`, response.data.new_password);
				});
			})
			.catch((error) => {
				setToast({
					open: true,
					severity: 'error',
					message: 'User Account creation failed. ' + error,
					clickToClose: true,
					duration: 3000,
				});
				console.log(`User Account creation failed: ${error}`);
			});
	}

	// If the password reset email fails, display a temporary password for the admin to give to the new user
	function emailSendFailure(user, tempPassword) {
		setToast({ open: true, severity: 'error', message: 'Error trying to send an email to the new user with a login link.', duration: 5000, clickToClose: true });
		setTempPassOpen({
			open: true,
			user: user,
			password: tempPassword,
		});
	}

	// Handle the location access toggle switches
	const handlePermissionToggle = (id, permission) => {
		setLocationAccess(prevAccess =>
			prevAccess.map(access => {
				if (access.location_id === id) {
					const updatedAccess = { ...access, [permission]: !access[permission] };
					if (permission !== 'has_access' && updatedAccess[permission]) {
						updatedAccess.has_access = true;
					}
					return updatedAccess;
				}
				return access;
			})
		);
	};

	// Handle the Select All button click - using functional updates to avoid dependencies
	const handleSelectAll = useCallback(() => {
		// Use functional updates to avoid depending on current state values
		setSelectAllState((currentState) => {
			// Filtered and sorted rows from the DataGrid
			const filteredRows = gridFilteredSortedRowEntriesSelector(apiRef)?.map((row) => row.model) || [];
			const startIndex = paginationModel.page * paginationModel.pageSize;
			const endIndex = startIndex + paginationModel.pageSize;
			const visibleRows = filteredRows.slice(startIndex, endIndex);

			const updatedLocationIDs = visibleRows.map(row => row.location_id);

			// Apply currentState to rows (currentState represents the current action to perform)
			setLocationAccess(prevAccess =>
				prevAccess.map(access => updatedLocationIDs.includes(access.location_id) ? { ...access, has_access: currentState } : access)
			);

			// Flip the state for the next click
			return !currentState;
		});
	}, [apiRef, paginationModel]);

	// Handle the Superuser switch - If superuser is enabled, set admin to true
	const handleIsSuperuserChange = () => {
		if (!isSuperuser && window.confirm('Are you sure you want to make this user a Superuser?')) {
			setIsAdmin(true);
			setIsSuperuser(true);
		} else {
			setIsSuperuser(false);
		}
	}

	// Handle dialog closing
	const handleClose = () => {
		setDialogOpen(false);
		clearDialog();
		setAccessStep(false);
		setSelectAllState(true);
	};

	// Handle button clicks
	const handleSaveClick = () => {
		if (infoRequirementsMet) {

			// Check if the user has access to at least one location, or is a superuser
			const hasAccess = locationAccess.some(access => access.has_access);
			if ( hasAccess || (creatorUser.is_superuser && isSuperuser) ) {
				createUser();
				handleClose();
			} else {
				setToast({
					open: true,
					severity: 'error',
					message: `${firstName} ${lastName} must have view access to at least one location.`,
					clickToClose: true,
					duration: 3000,
				});
			}
		} else {
			setToast({
				open: true,
				severity: 'error',
				message: 'New User Account info doesn\'t meet minimum requirements.',
				clickToClose: true,
				duration: 3000,
			});
		}
	};

	const handleBackClick = () => {
		setAccessStep(false);
	};

	const handleNextClick = () => {
		if (infoRequirementsMet) {
			setAccessStep(true);
		} else {
			setToast({
				open: true,
				severity: 'error',
				message: 'New User Account info doesn\'t meet minimum requirements.',
				clickToClose: true,
				duration: 3000,
			});
		}
	};

	const handleCancelClick = () => {
		handleClose();
	};

	// Dialog Content for the New User details
	const newUserPageContent = (
		<DialogContent>
			<Paper style={{ padding: 8, marginBottom: 8, border: '1px solid lightgray' }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Grid container spacing={1}>
						<Grid item xs={12}><Typography variant="h6">New User Info</Typography></Grid>
						<Grid item xs={6}>
							<TextField
								autoFocus
								margin="dense"
								id="first_name"
								label="First Name"
								type='text'
								fullWidth
								value={firstName}
								onChange={(event) => setFirstName(event.target.value)}
								required
								error={!firstNameValid}
								helperText={!firstNameValid ? 'Please enter a valid first name' : ''}
								inputProps={{ maxLength: 50 }}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								margin="dense"
								id="last_name"
								label="Last Name"
								type='text'
								fullWidth
								value={lastName}
								onChange={(event) => setLastName(event.target.value)}
								required
								error={!lastNameValid}
								helperText={!lastNameValid ? 'Please enter a valid last name' : ''}
								inputProps={{ maxLength: 50 }}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								margin="dense"
								id="email"
								label="Email Address"
								type='email'
								fullWidth
								value={email}
								onChange={(event) => setEmail(event.target.value)}
								required
								error={!emailValid}
								helperText={!emailValid ? 'Invalid email address' : ''}
								inputProps={{ maxLength: 64 }}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								margin="dense"
								id="job_title"
								label="Job Title"
								type='text'
								fullWidth
								value={jobTitle}
								onChange={(event) => setJobTitle(event.target.value)}
								inputProps={{ maxLength: 64 }}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								margin="dense"
								id="company"
								label="Company"
								type='text'
								fullWidth
								value={company}
								onChange={(event) => setCompany(event.target.value)}
								inputProps={{ maxLength: 64, }}
							/>
						</Grid>
					</Grid>
				</Box>
			</Paper>
			<Paper style={{ padding: 8, marginBottom: 8, border: '1px solid lightgray' }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Grid container spacing={1}>
						<Grid item xs={12}><Typography variant="h6">New User Preferences</Typography></Grid>
						<Grid item xs={4} sm={3}>
							<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>Time Zone:</Typography>
							</Box>
						</Grid>
						<Grid item xs={8} sm={9}>
							<Tooltip placement='right' title='Choose the timezone that all dates on the site will be shown in for the new user'>
								<Select
									value={timeZone}
									error={!timeZoneValid}
									onChange={(e) => setTimeZone(e.target.value)}
									size='small'
									style={{ width: '100%' }}
								>
									{TIME_ZONES.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</Tooltip>
						</Grid>
						<Grid item xs={4} sm={3}>
							<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>Units:</Typography>
							</Box>
						</Grid>
						<Grid item xs={8} sm={9}>
							<Stack direction="row" spacing={1} alignItems="center" flexGrow={false}>
								<Typography fontFamily='Saira, sans-serif'>Imperial</Typography>
									<Tooltip placement='right' title='Choose if the new user would prefer Imperial or Metric measurement units. (They can change this in the future)'>
										<Switch checked={metric} onChange={() => setMetric(!metric)} />
									</Tooltip>
								<Typography fontFamily='Saira, sans-serif'>Metric</Typography>
							</Stack>
						</Grid>
					</Grid>
				</Box>
			</Paper>
			<Paper style={{ padding: 8, marginBottom: 8, border: '1px solid lightgray' }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Grid container spacing={1}>
						<Grid item xs={12}><Typography variant="h6">Account Access</Typography></Grid>
						{/* is_active */}
						<Grid item xs={4} sm={3}>
							<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>Account Active</Typography>
							</Box>
						</Grid>
						<Grid item xs={8} sm={9}>
							<Tooltip placement='right' title='Choose if the new user should have access as soon as the account is created'>
								<Switch checked={isActive} onChange={() => setIsActive(!isActive)} />
							</Tooltip>
						</Grid>
						{/* is_staff */}
						<Grid item xs={4} sm={3}>
							<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>Is Admin:</Typography>
							</Box>
						</Grid>
						<Grid item xs={8} sm={9}>
							<Tooltip placement='right' title='Should the new user have Administrator access? (Administrators can manage User accounts associated with the Locations assigned to them)'>
								<Switch checked={isAdmin} onChange={() => { setIsAdmin(!isAdmin) }} disabled={isSuperuser}/>
							</Tooltip>
						</Grid>
						{/* is_superuser - only visible if the creating user is a superuser themself*/}
						{ creatorUser.is_superuser && (
							<>
							<Grid item xs={4} sm={3}>
								<Box display="flex" alignItems="center" height="100%">
									<Typography fontFamily='Saira, sans-serif'>Is Superuser:</Typography>
								</Box>
							</Grid>
							<Grid item xs={8} sm={9}>
								<Tooltip placement='right' title='Should the new user be a Superuser? (Superusers can access/change anything on the site)'>
									<Switch checked={isSuperuser}  onChange={handleIsSuperuserChange} />
								</Tooltip>
							</Grid>
							</>
						)}
					</Grid>
				</Box>
			</Paper>
		</DialogContent>
	);

	// Dialog Content for the New User location access
	const locationAccessPageContent = (
		<DialogContent>
			<Paper style={{ padding: 8, marginBottom: 8, border: '1px solid lightgray' }}>
				<Box height='65vh' width='100%' >
					<style>{`
						.row-color-748592 { background-color: #74859299 !important; }
						.row-color-4b9c2e { background-color: #4b9c2e99 !important; }
						.row-color-f4b235 { background-color: #f4b23599 !important; }
						.row-color-f22635 { background-color: #f2263599 !important; }
					`}</style>
					<DataGrid

						apiRef={apiRef}
						getRowId={(row) => row.location_id}
						rows={combinedData}
						columns={columns}
						pagination
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						pageSizeOptions={[10, 25, 50, 100]}
						density='compact'
						getRowClassName={getRowClassName}
						slots={{
							toolbar: CreateUserLocationAccessToolbar,
							noRowsOverlay: NoItemsOverlay,
						}}
						slotProps={{
							toolbar: {
								onSelectAll: handleSelectAll,
								selectAllState: selectAllState,
								onHierarchyFilterChange: handleHierarchyFilterChange,
							},
							noRowsOverlay: { noItemsText: 'No Locations' },
						}}
						disableSelectionOnClick
					/>
				</Box>
			</Paper>
		</DialogContent>
	);

	return (
		<>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
				clickToClose={toast.clickToClose}
				duration={toast.duration}
			/>
			<TempPassDialog
				tempPassOpen={tempPassOpen.open}
				setTempPassOpen={() => setTempPassOpen({ open: false, user: '', password: '' })}
				username={tempPassOpen.user}
				tempPassword={tempPassOpen.password}
			/>
			<Dialog
				open={dialogOpen}
				onClose={handleClose}
				aria-labelledby="form-dialog-title"
				maxWidth={ accessStep ? 'xl' : 'sm' }
				fullWidth
				sx={{
					'.MuiDialog-paper': {
						border: '5px solid #B1282E',
						borderRadius: 3
					},
				}}
			>
				<DialogTitle id="form-dialog-title">
					<Typography variant="h4" component="div" fontFamily='Saira, sans-serif' fontWeight={600} textAlign='center' sx={{ flexGrow: 1 }}>
						{ !accessStep ?
							'Create New User' :
							firstName + ' ' + lastName + ' Location Access'
						}
					</Typography>
				</DialogTitle>
				{ !accessStep ? newUserPageContent : locationAccessPageContent }
				<DialogActions sx={{ justifyContent: 'center', paddingBottom: 3 }}>
					<Button onClick={handleCancelClick} variant='outlined' size='large'>
						Cancel
					</Button>
					{/* Back button will show up if on the location access permissions step */}
					{ accessStep && (
						<Button onClick={handleBackClick} variant='outlined' size='large'>
							Back
						</Button>
					)}
					{/* Save button will show if on the location access permissions step, or on the info step and the new user is a Superuser */}
					{ ( accessStep || (creatorUser.is_superuser && isSuperuser) ) ? (
						<Button onClick={handleSaveClick} variant='contained' size='large' disabled={!infoRequirementsMet}>
							Save
						</Button>
					) : (
						<Button onClick={handleNextClick} variant='contained' size='large' disabled={!infoRequirementsMet}>
							Next
						</Button>
					) }
				</DialogActions>
			</Dialog>
		</>
	);
}